/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/live.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useEffect, useSyncExternalStore } from 'react';

/**
 * Live updates over Server-Sent Events. One EventSource for the whole app; every audited mutation
 * on the server arrives as a `change` for its Mandant (bursts coalesced). Pages add `useLive(tid)`
 * to their data dependencies and refetch; the previous render stays on screen meanwhile.
 */

interface LiveState {
  connected: boolean; seq: Record<string, number>; any: number; last: { tenantId: string; actions: string[]; at: string } | null;
  /** Per Mandant and topic (the audit action up to its first dot: «question», «receipts», «client»): a change counter. */
  topics: Record<string, number>;
}

let state: LiveState = { connected: false, seq: {}, any: 0, last: null, topics: {} };
const listeners = new Set<() => void>();
let es: EventSource | null = null;
let users = 0;
let retry: ReturnType<typeof setTimeout> | null = null;

const set = (patch: Partial<LiveState>) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };

function connect() {
  if (es || typeof EventSource === 'undefined') return;
  es = new EventSource('/api/live');
  es.addEventListener('hello', () => set({ connected: true }));
  es.addEventListener('change', (m) => {
    try {
      const e = JSON.parse((m as MessageEvent).data) as { tenantId: string; actions: string[]; at: string };
      const topics = { ...state.topics };
      for (const t of new Set(e.actions.map((a) => a.split('.')[0]))) topics[`${e.tenantId}|${t}`] = (topics[`${e.tenantId}|${t}`] ?? 0) + 1;
      set({ seq: { ...state.seq, [e.tenantId]: (state.seq[e.tenantId] ?? 0) + 1 }, any: state.any + 1, last: e, topics });
    } catch { /* malformed event: ignore */ }
  });
  es.onerror = () => {
    set({ connected: false });
    // The browser retries on its own; if the stream was closed for good, reopen with a pause.
    if (es?.readyState === EventSource.CLOSED) { es = null; if (users && !retry) retry = setTimeout(() => { retry = null; connect(); }, 3000); }
  };
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  if (users++ === 0) connect();
  return () => {
    listeners.delete(cb);
    if (--users === 0) {
      if (retry) { clearTimeout(retry); retry = null; }
      es?.close(); es = null; set({ connected: false });
    }
  };
}

/** Change counter for one Mandant (or for any Mandant when `tid` is null). */
export function useLive(tid: string | null) {
  return useSyncExternalStore(subscribe, () => (tid ? state.seq[tid] ?? 0 : state.any));
}

/**
 * Change counter for one Mandant, counting only changes whose audit actions start with one of `topics` (e.g.
 * «question» for question.answered): a card refetches when a client answers elsewhere, not on every booking.
 */
export function useLiveTopic(tid: string | null, ...topics: string[]) {
  return useSyncExternalStore(subscribe, () => (tid ? topics.reduce((n, t) => n + (state.topics[`${tid}|${t}`] ?? 0), 0) : 0));
}

export function useLiveStatus() {
  return useSyncExternalStore(subscribe, () => state.connected);
}

/** Run `fn` after changes to a Mandant, at most once per `ms` (for lists outside useApi). */
export function useOnLive(tid: string | null, fn: () => void, ms = 600) {
  const n = useLive(tid);
  useEffect(() => {
    if (!n) return;
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n]);
}
